import styled from '@emotion/styled';
import { ChangeEvent, MouseEvent, useEffect, useState } from 'react';
import { formatDate } from '../../util/deals-util';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Chip from '@mui/material/Chip';
import { buyerSeatService } from '../../api/services/buyer-seat.service';
import { BuyerSeatOverviewDto } from '../../api/services/dto/buyer-seat-dto';
import { useAlert } from '../shared/AlertProvider';
import StatsTile from '../shared/stats-tiles/StatsTile';
import StatsTilesRow from '../shared/stats-tiles/StatsTilesRow';
import { applicationService } from '../../api/services/application.service';
import BuyerSeatBreadcrumb from './BuyerSeatBreadcrumb';
import { useWorkspace } from '../../context/WorkspaceContext';

interface PaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }: PaginationActionsProps) => (
  <Box sx={{ flexShrink: 0, ml: 2.5 }}>
    <IconButton
      onClick={(e) => onPageChange(e, page - 1)}
      disabled={page === 0}
      aria-label="previous page"
    >
      <KeyboardArrowLeft />
    </IconButton>
    <IconButton
      onClick={(e) => onPageChange(e, page + 1)}
      disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      aria-label="next page"
    >
      <KeyboardArrowRight />
    </IconButton>
  </Box>
);

type SortDir = 'asc' | 'desc';

const BuyerSeatList = () => {
  const { setAlert } = useAlert();
  const navigate = useNavigate();
  const { workspace } = useWorkspace();

  const [buyerSeats, setBuyerSeats] = useState<BuyerSeatOverviewDto[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [activeCount, setActiveCount] = useState<number>(0);
  const [dealsCount, setDealsCount] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [totalItems, setTotalItems] = useState<number>(0);

  const fetchBuyerSeats = (
    searchTerm?: string,
    status?: string,
    currentPage?: number,
    size?: number,
    dir?: SortDir,
  ) => {
    buyerSeatService
      .getBuyerSeatList({
        search: searchTerm || undefined,
        status: status || undefined,
        page: currentPage ?? page,
        size: size ?? rowsPerPage,
        sort: `auditMetadata.createdDate,${dir ?? sortDir}`,
      })
      .then((res) => {
        setBuyerSeats(res.buyerSeats);
        setTotalItems(res.total);
      })
      .catch((reason) =>
        setAlert({
          open: true,
          severity: 'error',
          title: 'Failed to fetch buyer seats',
          message: reason.message,
        })
      );
  };

  useEffect(() => {
    if (!workspace) return;
    buyerSeatService.getBuyerSeatCount().then(setTotalCount).catch(() => {});
    buyerSeatService.getActiveBuyerSeatCount().then(setActiveCount).catch(() => {});
    applicationService.getDealCount().then(setDealsCount).catch(() => {});
  }, [workspace]);

  useEffect(() => {
    if (!workspace) return;
    let timer: NodeJS.Timeout | undefined;
    setPage(0);
    if (search) {
      timer = setTimeout(() => fetchBuyerSeats(search, statusFilter, 0, rowsPerPage, sortDir), 500);
    } else {
      fetchBuyerSeats(undefined, statusFilter, 0, rowsPerPage, sortDir);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [search, statusFilter, sortDir, workspace]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchBuyerSeats(search, statusFilter, newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchBuyerSeats(search, statusFilter, 0, newRowsPerPage);
  };

  return (
    <ListPageWrapper>
      <BuyerSeatBreadcrumb items={[{ label: 'Buyer Seats' }]} />

      <SearchAndActionsWrapper>
        <FiltersWrapper>
          <TextField
            placeholder="Search buyer seats"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 280,
              '& .MuiOutlinedInput-root': { backgroundColor: '#F5F5F5' },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {search && (
                    <IconButton
                      size="small"
                      onClick={() => setSearch('')}
                      sx={{ p: 0.5, mr: 0.5 }}
                    >
                      <ClearIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </IconButton>
                  )}
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 140,
              '& .MuiOutlinedInput-root': { backgroundColor: '#F5F5F5' },
            }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
            <MenuItem value="DISABLED">Disabled</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            value={sortDir}
            onChange={(e) => setSortDir(e.target.value as SortDir)}
            variant="outlined"
            sx={{
              minWidth: 140,
              '& .MuiOutlinedInput-root': { backgroundColor: '#F5F5F5' },
            }}
          >
            <MenuItem value="desc">Date: Newest</MenuItem>
            <MenuItem value="asc">Date: Oldest</MenuItem>
          </TextField>
        </FiltersWrapper>

        <Button
          color="primary"
          variant="contained"
          size="large"
          onClick={() => navigate('/buyer-seat')}
        >
          Add Buyer Seat
        </Button>
      </SearchAndActionsWrapper>

      <StatsTilesRow>
        <StatsTile label="Total Seats" value={totalCount} />
        <StatsTile label="Active Seats" value={activeCount} />
        <StatsTile label="Total Deals" value={dealsCount} />
      </StatsTilesRow>

      <TableContainer sx={{ backgroundColor: '#ffffff' }}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: '#F5F5F5', borderTop: '1px solid #0000001A' }}>
              <TableCell>Buyer Seat / Member</TableCell>
              <TableCell>Buyer Seat ID</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>SSP</TableCell>
              <TableCell style={{ width: 80 }}>Deals</TableCell>
              <TableCell style={{ width: 100 }}>Status</TableCell>
              <TableCell style={{ width: 160 }}>
                  Date Created
              </TableCell>
              <TableCell style={{ width: 80 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {buyerSeats.length ? (
              buyerSeats.map((seat) => (
                <TableRow key={seat.id}>
                  <TableCell>{seat.name}</TableCell>
                  <TableCell>{seat.buyerSeatId}</TableCell>
                  <TableCell>{seat.organization || '—'}</TableCell>
                  <TableCell>{seat.ssp}</TableCell>
                  <TableCell>{seat.dealsCount}</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        seat.status === 'ACTIVE'
                          ? 'Active'
                          : seat.status === 'DISABLED'
                          ? 'Disabled'
                          : 'Inactive'
                      }
                      className={`table-chip ${
                        seat.status === 'ACTIVE'
                          ? 'organization-status-active'
                          : 'organization-status-inactive'
                      }`}
                    />
                  </TableCell>
                  <TableCell>
                    {formatDate(seat.createdAt)}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/buyer-seat/list/${seat.id}`)}
                      title="View details"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8}>
                  <NoMatches>No buyer seats found</NoMatches>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {buyerSeats.length ? (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  colSpan={8}
                  count={totalItems}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{ native: true }}
                  onPageChange={(_e: MouseEvent<HTMLButtonElement> | null, newPage: number) =>
                    handlePageChange(newPage)
                  }
                  onRowsPerPageChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleRowsPerPageChange(parseInt(e.target.value, 10))
                  }
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          ) : null}
        </Table>
      </TableContainer>
    </ListPageWrapper>
  );
};

export default BuyerSeatList;

const ListPageWrapper = styled.div`
  width: 100%;
  max-width: 1440px;
  height: 100%;
`;

const SearchAndActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 16px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NoMatches = styled.div`
  font-size: 16px;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 20px;
`;
